
// vue
import { ref, reactive, defineComponent, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

// firebase
import 'firebase/firestore';
import { db, serverTimestamp, arrayUnion, arrayRemove } from '@/main';

// atoms
import Card from '@/components/atoms/Card.vue';
import CustomButton from '@/components/atoms/CustomButton.vue';
import Divider from '@/components/atoms/Divider.vue';
import Loader from '@/components/atoms/Loader.vue';
import ProgressBar from '@/components/atoms/ProgressBar.vue';
import TextField from '@/components/atoms/TextField.vue';
import MultilineField from '@/components/atoms/MultilineField.vue';
// molecules
import HeaderMenu from '@/components/molecules/HeaderMenu.vue';
// organisms
import ChatArea from '@/components/organisms/ChatArea.vue';
import ExpiredScreen from '@/components/organisms/ExpiredScreen.vue';
import HamburgerMenu from '@/components/organisms/HamburgerMenu.vue';
import LaunchAdminScreen from '@/components/organisms/LaunchAdminScreen.vue';

// functions
import {
  judgeShowLaunchScreen,
  isExceedRetryTypeCnt,
  isPWAuthenticated,
  getPostTime,
} from '@/functions/pages/Admin';

// utils
import {
  calcDiffTime,
  calcPercentage,
  convertDateToString,
  getRoomId,
  isExpired,
  isNotContainRoomInfo,
  initList,
  initObj,
  initLastFetchDate,
  sha256,
  // Dividerで利用
  leftWidth,
  changeWidth,
} from '@/utils';

// types
import { RoomDoc } from '@/types';
import { store } from '@/store';

export default defineComponent({
  components: {
    // atoms
    Card,
    CustomButton,
    Divider,
    Loader,
    ProgressBar,
    TextField,
    MultilineField,
    // molecules
    HeaderMenu,
    // organisms
    ChatArea,
    ExpiredScreen,
    HamburgerMenu,
    LaunchAdminScreen,
  },
  setup() {
    const route = useRoute();
    const questionTxt = ref('');
    const openHamburgerMenu = ref(false);

    const roomId = getRoomId(route);

    const inputPassword = ref('');
    const showTypePassScreen = ref(true);
    const showExpiredScreen = ref(false);

    const checkPassword = async (inputPassword: string) => {
      // 入力回数制限
      if (isExceedRetryTypeCnt(10)) {
        alert(
          '入力回数の上限に達しました。暫く時間を空けてからお試しください。'
        );
        return;
      }

      // ルームパスワードの一致確認
      const pw = await sha256(inputPassword);
      if (pw === roomObj?.roomPassword) {
        showTypePassScreen.value = false;
        sessionStorage.setItem(`p${roomId}`, pw);
      } else {
        alert('パスワードが違います');
      }
    };

    const isLoading = ref(true);
    const roomObj = reactive({}) as RoomDoc;

    db.collection('Room')
      .doc(roomId)
      .get()
      .then((doc) => {
        const data = doc.data() as RoomDoc;
        roomObj.roomTitle = data.roomTitle;
        roomObj.studentNum = data.studentNum;
        roomObj.expirationDate = data.expirationDate;
        roomObj.roomPassword = data.roomPassword;
        roomObj.createdAt = data.createdAt;
        roomObj.updatedAt = data.updatedAt;

        // PW認証済みじゃない場合は、PW入力画面を表示
        if (isPWAuthenticated(route, roomId, roomObj.roomPassword)) {
          showTypePassScreen.value = false;
        }

        // ルーム期限チェック
        const createdAt = roomObj.createdAt.toDate();
        const expirationDate = roomObj.expirationDate;
        showExpiredScreen.value = isExpired(createdAt, expirationDate);
      })
      .catch((e) => {
        // 必要な情報が取得できていない場合は利用不可にする
        if (isNotContainRoomInfo(roomObj)) {
          showExpiredScreen.value = true;
        }
        console.error(e);
      })
      .finally(() => {
        isLoading.value = false;
      });

    // 問題一覧の初期値を取得（キャッシュor空）
    const questionIdList = reactive(initList(roomId, 'questionIdList'));
    const questionObj = reactive(initObj(roomId, 'questionObj'));
    const lastFetchQuestionDate = new Date(2020, 1, 1); //TODO: キャッシュがあればry
    // const lastFetchQuestionDate = initLastFetchDate(
    //   id,
    //   'lastFetchQuestionDate'
    // );

    const questionsUnsubscribe = ref<any>(null);
    questionsUnsubscribe.value = db
      .collection('Room')
      .doc(roomId)
      .collection('questions')
      .where('createdAt', '>', lastFetchQuestionDate)
      .orderBy('createdAt')
      .onSnapshot((snapShot) => {
        sessionStorage.setItem(
          `lastFetchQuestionDate${roomId}`,
          String(new Date())
        );
        snapShot.forEach((doc) => {
          if (!questionIdList.includes(doc.id)) {
            questionIdList.push(doc.id);
          }

          questionObj[doc.id] = doc.data();
        });

        sessionStorage.setItem(
          `questionIdList${roomId}`,
          JSON.stringify(questionIdList)
        );
        store.commit('setQuestionIdList', questionIdList);

        sessionStorage.setItem(
          `questionObj${roomId}`,
          JSON.stringify(questionObj)
        );
        store.commit('setQuestionObj', questionObj);
      });

    const isAdding = ref(false);
    const addQuestion = async () => {
      if (questionTxt.value.length === 0) return;
      if (isAdding.value) return;

      isAdding.value = true;

      try {
        const doc = await db
          .collection('Room')
          .doc(roomId)
          .collection('questions')
          .add({
            body: questionTxt.value,
            completedUsers: [],
            completedUserInfo: {},
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

        await db
          .collection('Room')
          .doc(roomId)
          .update({
            questionIdList: arrayUnion(doc.id),
            updatedAt: serverTimestamp(),
          });

        questionTxt.value = '';
      } catch (e) {
        console.error(e);
      }

      isAdding.value = false;
    };

    const editQuestion = (questionId: string, event: HTMLElement) => {
      console.log('時間ができたら実装する');
    };

    const deleteQuestion = async (questionId: string) => {
      if (!confirm('一度削除したら元に戻せませんが、よろしいですか？')) return;

      try {
        await db
          .collection('Room')
          .doc(roomId)
          .collection('questions')
          .doc(questionId)
          .delete();

        await db
          .collection('Room')
          .doc(roomId)
          .update({
            questionIdList: arrayRemove(questionId),
            updatedAt: serverTimestamp(),
          });

        // ID配列から削除
        const deleteIndex = questionIdList.indexOf(questionId);
        questionIdList.splice(deleteIndex, 1);
        sessionStorage.setItem(
          `questionIdList${roomId}`,
          JSON.stringify(questionIdList)
        );
        store.commit('setQuestionIdList', questionIdList);

        // オブジェクト一覧から削除
        delete questionObj[questionId];
        sessionStorage.setItem(
          `questionObj${roomId}`,
          JSON.stringify(questionObj)
        );
        store.commit('setQuestionObj', questionObj);
      } catch (e) {
        console.error(e);
      }
    };

    interface Details {
      [key: string]: boolean;
    }
    const showDetails = reactive<Details>({});
    const toggleShowDetails = (index: number) => {
      const showDetail = showDetails[index];
      // 初めて展開する場合は初期化
      if (showDetail === undefined) showDetails[index] = false;

      if (showDetail) {
        showDetails[index] = false;
      } else {
        showDetails[index] = true;
      }
    };

    /**
     * 対象の差分を求める
     * @param {Number} target 比較先
     * @param {Number} origin 比較元
     */
    const calcDiffTarget = (
      questionId: string,
      target: number,
      origin: number
    ) => {
      const question = questionObj[questionId];

      const targetUserId = question.completedUsers[target];
      const targetTime = question.completedUserInfo[targetUserId].timeStamp;
      const originUserId = question.completedUsers[origin];
      const originTime = question.completedUserInfo[originUserId].timeStamp;

      const targetDate = getPostTime(targetTime);
      const originDate = getPostTime(originTime);

      return calcDiffTime(targetDate, originDate);
    };

    // ルーム作成直後の遷移かどうか
    const showLaunchScreen = ref(judgeShowLaunchScreen(roomId));

    onUnmounted(() => {
      questionsUnsubscribe.value();
    });

    return {
      isLoading,
      showLaunchScreen,
      roomId,
      checkPassword,
      showTypePassScreen,
      inputPassword,
      questionTxt,
      questionIdList,
      questionObj,
      addQuestion,
      editQuestion,
      deleteQuestion,
      calcPercentage,
      roomObj,
      openHamburgerMenu,
      getPostTime,
      convertDateToString,
      calcDiffTarget,
      // 期限切れの表示用
      showExpiredScreen,
      // コンテンツの横幅
      leftWidth,
      changeWidth,
      // ラップタイム表示のトグル
      showDetails,
      toggleShowDetails,
    };
  },
});
